import React from 'react';
import keyBy from 'lodash/keyBy';
import queryString from 'query-string';
import format from 'xml-formatter';
import { DateTimeFormatter, DateTimeFormats } from 'brock-date-time';
import { icons } from 'smartsuite-ui';
import LinkWrapper from './LinkWrapper';
import {
    getAdvancedSearchLinkWithParametersPath,
    getAirWaybillDetailsLinkPath,
    getBagDetailsLinkPath,
    getBidsLateralLinkPath,
    getCargoPieceDetailsLinkPath,
    getContainerDetailsLinkPath,
    getFlightDetailsLinkPath,
    getLocationDetailsLinkPath,
    getPassengerDetailsLinkWithBookingIdPath,
    getPassengerDetailsLinkWithPassengerIdPath,
} from '../../constants/ApiConstants';

function isValueAnObject(dataObject) {
    return typeof dataObject === 'object' && dataObject !== null;
}

function isStringTagBased(preformedText) {
    const flattenedAndTrimmedPreformedText = preformedText
        .replace(/[\r\n]+/gm, '')
        .trim();
    return (
        flattenedAndTrimmedPreformedText.substring(0, 1) === '<' &&
        flattenedAndTrimmedPreformedText.includes('><') &&
        flattenedAndTrimmedPreformedText[
            flattenedAndTrimmedPreformedText.length - 1
        ] === '>'
    );
}

function renderPreformedText(preformedText) {
    if (isStringTagBased(preformedText)) {
        const options = {
            indentation: '    ',
            stripComments: false,
            collapseContent: true,
        };

        try {
            return format(preformedText, options);
        } catch {
            return preformedText;
        }
    }

    return preformedText;
}

function isColumnDataParsable(columnData) {
    return (
        !!columnData ||
        columnData === 0 ||
        Object.is(columnData, -0) ||
        columnData === false ||
        columnData === ''
    );
}

export default (resultsConfiguration) => {
    const resultsConfigurationKeyedByFieldName = keyBy(
        resultsConfiguration,
        'fieldName'
    );
    return (columnKey, rowDataObject) => {
        if (!isColumnDataParsable(rowDataObject[columnKey])) {
            return '';
        }

        switch (resultsConfigurationKeyedByFieldName[columnKey].renderType) {
            case 'DATETIME': {
                return DateTimeFormatter.withFormatString(
                    rowDataObject[columnKey],
                    DateTimeFormats.DATETIME_SECONDS
                );
            }

            case 'PREFORMATTED_TEXT': {
                return (
                    <p className="advanced-search__preformatted-text">
                        {renderPreformedText(rowDataObject[columnKey])}
                    </p>
                );
            }

            case 'BAG_DETAILS_LINK': {
                if (isValueAnObject(rowDataObject[columnKey])) {
                    const { id, hasAccess, value } = rowDataObject[columnKey];
                    return (
                        <LinkWrapper
                            pathName={getBagDetailsLinkPath(id)}
                            destinationSystemKey={
                                resultsConfigurationKeyedByFieldName[columnKey]
                                    .destinationSystemKey
                            }
                            hasAccess={hasAccess}
                            openInNewTab={true}
                        >
                            {value}
                        </LinkWrapper>
                    );
                }
                return rowDataObject[columnKey];
            }

            case 'FLIGHT_DETAILS_LINK': {
                if (isValueAnObject(rowDataObject[columnKey])) {
                    const { id, hasAccess, value } = rowDataObject[columnKey];
                    return (
                        <LinkWrapper
                            pathName={getFlightDetailsLinkPath(id)}
                            destinationSystemKey={
                                resultsConfigurationKeyedByFieldName[columnKey]
                                    .destinationSystemKey
                            }
                            hasAccess={hasAccess}
                            openInNewTab={true}
                        >
                            {value}
                        </LinkWrapper>
                    );
                }

                return rowDataObject[columnKey];
            }

            case 'CONTAINER_DETAILS_LINK': {
                if (isValueAnObject(rowDataObject[columnKey])) {
                    const { id, hasAccess, value } = rowDataObject[columnKey];
                    return (
                        <LinkWrapper
                            pathName={getContainerDetailsLinkPath(id)}
                            destinationSystemKey={
                                resultsConfigurationKeyedByFieldName[columnKey]
                                    .destinationSystemKey
                            }
                            hasAccess={hasAccess}
                            openInNewTab={true}
                        >
                            {value}
                        </LinkWrapper>
                    );
                }

                return rowDataObject[columnKey];
            }

            case 'PASSENGER_DETAILS_LINK_WITH_BOOKING_ID': {
                if (isValueAnObject(rowDataObject[columnKey])) {
                    const { id, hasAccess, value } = rowDataObject[columnKey];
                    return (
                        <LinkWrapper
                            pathName={getPassengerDetailsLinkWithBookingIdPath(
                                id
                            )}
                            destinationSystemKey={
                                resultsConfigurationKeyedByFieldName[columnKey]
                                    .destinationSystemKey
                            }
                            hasAccess={hasAccess}
                            openInNewTab={true}
                        >
                            {value}
                    </LinkWrapper>
                    );
                }

                return rowDataObject[columnKey];
            }

            case 'PASSENGER_DETAILS_LINK_WITH_PASSENGER_ID': {
                if (isValueAnObject(rowDataObject[columnKey])) {
                    const { id, hasAccess, value } = rowDataObject[columnKey];
                    return (
                        <LinkWrapper
                            pathName={getPassengerDetailsLinkWithPassengerIdPath(
                                id
                            )}
                            destinationSystemKey={
                                resultsConfigurationKeyedByFieldName[columnKey]
                                    .destinationSystemKey
                            }
                            hasAccess={hasAccess}
                            openInNewTab={true}
                        >
                            {value}
                        </LinkWrapper>
                    );
                }

                return rowDataObject[columnKey];
            }

            case 'AIR_WAYBILL_DETAILS_LINK': {
                if (isValueAnObject(rowDataObject[columnKey])) {
                    const { id, hasAccess, value } = rowDataObject[columnKey];
                    return (
                        <LinkWrapper
                            pathName={getAirWaybillDetailsLinkPath(id)}
                            destinationSystemKey={
                                resultsConfigurationKeyedByFieldName[columnKey]
                                    .destinationSystemKey
                            }
                            hasAccess={hasAccess}
                            openInNewTab={true}
                        >
                            {value}
                        </LinkWrapper>
                    );
                }

                return rowDataObject[columnKey];
            }

            case 'CARGO_PIECE_DETAILS_LINK': {
                if (isValueAnObject(rowDataObject[columnKey])) {
                    const { id, hasAccess, value } = rowDataObject[columnKey];
                    return (
                        <LinkWrapper
                            pathName={getCargoPieceDetailsLinkPath(id)}
                            destinationSystemKey={
                                resultsConfigurationKeyedByFieldName[columnKey]
                                    .destinationSystemKey
                            }
                            hasAccess={hasAccess}
                            openInNewTab={true}
                        >
                            {value}
                        </LinkWrapper>
                    );
                }

                return rowDataObject[columnKey];
            }

            case 'HOT_ICON_WITH_TEXT': {
                if (isValueAnObject(rowDataObject[columnKey])) {
                    const { isHot, value } = rowDataObject[columnKey];

                    if (isHot) {
                        return (
                            <div className="horizontal-flex">
                                <div className="time-classification-cell">
                                    <span className={icons.hot} />
                                </div>
                                <span>{value}</span>
                            </div>
                        );
                    }

                    return <span>{value}</span>;
                }

                return rowDataObject[columnKey];
            }

            case 'LOCATION_DETAILS_LINK': {
                if (isValueAnObject(rowDataObject[columnKey])) {
                    const {
                        stationId,
                        areaId,
                        locationId,
                        hours,
                        hasAccess,
                        value,
                    } = rowDataObject[columnKey];
                    return (
                        <LinkWrapper
                            pathName={getLocationDetailsLinkPath(
                                stationId,
                                areaId,
                                locationId,
                                hours
                            )}
                            destinationSystemKey={
                                resultsConfigurationKeyedByFieldName[columnKey]
                                    .destinationSystemKey
                            }
                            hasAccess={hasAccess}
                            openInNewTab={true}
                        >
                            {value}
                        </LinkWrapper>
                    );
                }

                return rowDataObject[columnKey];
            }

            case 'ADVANCED_SEARCH_LINK_WITH_PARAMETERS': {
                if (isValueAnObject(rowDataObject[columnKey])) {
                    const {
                        pageKey,
                        hasAccess,
                        parameters,
                        value,
                    } = rowDataObject[columnKey];

                    if (!hasAccess) {
                        return rowDataObject[columnKey];
                    }

                    const path =
                        getAdvancedSearchLinkWithParametersPath(pageKey);
                    const params = queryString.stringify(parameters);
                    const pathName = `${path}?${params}`;

                    return (
                        <LinkWrapper
                            pathName={pathName}
                            destinationSystemKey={
                                resultsConfigurationKeyedByFieldName[columnKey]
                                    .destinationSystemKey
                            }
                            hasAccess={hasAccess}
                            openInNewTab={true}
                        >
                            {value}
                        </LinkWrapper>
                    );
                }

                return rowDataObject[columnKey];
            }

            case 'BIDS_LATERAL_LINK': {
                if (isValueAnObject(rowDataObject[columnKey])) {
                    const { id, hasAccess, value } = rowDataObject[columnKey];

                    return (
                        <LinkWrapper
                            pathName={getBidsLateralLinkPath(id)}
                            destinationSystemKey={
                                resultsConfigurationKeyedByFieldName[columnKey]
                                    .destinationSystemKey
                            }
                            hasAccess={hasAccess}
                            openInNewTab={true}
                        >
                            {value}
                        </LinkWrapper>
                    );
                }

                return rowDataObject[columnKey];
            }

            default: {
                if (isValueAnObject(rowDataObject[columnKey])) {
                    return rowDataObject[columnKey].value;
                }
                return rowDataObject[columnKey];
            }
        }
    };
};
